import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { FullPageLoader } from './components/layout/RouteFallback';
import { ToastProvider } from './components/ui/Toast';
import { AuthProvider } from './features/auth/AuthProvider';
import { ApiError } from './lib/api-client';
import { AppRoutes } from './routes';

/**
 * A 401 is handled by the api client (refresh-then-retry); by the time an error
 * surfaces here the session is genuinely gone, and 4xx responses are business
 * answers rather than transient faults, so only 5xx and network errors are
 * worth retrying.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Financial mutations are idempotent by key, but an automatic retry of a
      // rejected bet is still never what the player asked for (TZ §35).
      retry: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Suspense fallback={<FullPageLoader />}>
              <AppRoutes />
            </Suspense>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
